package com.taocent.simple.compose.component

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFontFamilyResolver
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.platform.Font
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.ComposeViewport

// Fonts are fetched as standalone assets at <dist>/font/<name>.ttf by webpack 5's
// asset/resource rule (see webpack.config.d/02-emit-fonts-explicit.js, which lists
// each font as an entry to force emit on both js and wasmJs targets). The browser
// downloads them in parallel with the wasm bundle, and an index.html
// <link rel="preload" as="fetch"> starts the request before the wasm JS even runs.
private const val CHINESE_FONT_PATH = "font/pfm.ttf"
private const val EMOJI_FONT_PATH = "font/ace.ttf"

/**
 * 流式下载单个字体文件,字节进度通过 onStageChange(0..1) 回调。
 * fetchFontBytes 内部用 window.fetch + ReadableStream 拿真实字节级进度,UI 看上去连续平滑。
 */
private suspend fun loadFontBytes(path: String, onStageChange: (Float, String) -> Unit): ByteArray {
    onStageChange(0f, "Fetching $path")
    return fetchFontBytes(path) { fraction: Float ->
        onStageChange(fraction, "Fetching $path")
    }
}

/**
 * 加载阶段(报告 0f..1f 真实加载进度,带 preload 阶段)。
 * 使用 fetch + ReadableStream chunks 拿真实字节级进度(0..1),然后用 Compose 的 animateFloatAsState 做平滑过渡。
 */
private suspend fun loadFonts(
    resolver: FontFamily.Resolver,
    onProgress: (Float, String) -> Unit,
) {
    onProgress(0f, "Loading Chinese font…")
    val chineseBytes = loadFontBytes(CHINESE_FONT_PATH) { fraction, msg ->
        onProgress(fraction * 0.45f, msg)
    }
    onProgress(0.5f, "Loading Unicode emoji font…")
    val emojiBytes = loadFontBytes(EMOJI_FONT_PATH) { fraction, msg ->
        onProgress(0.5f + fraction * 0.4f, msg)
    }
    onProgress(0.92f, "Registering font loader…")

    val chineseFont = FontFamily(Font("pfm", chineseBytes))
    val emojiFont = FontFamily(Font("ace", emojiBytes))

    resolver.preload(chineseFont)
    onProgress(0.96f, "Registering Unicode emoji font…")
    resolver.preload(emojiFont)
    onProgress(1f, "Ready")
}

@Composable
private fun LoadingScreen(progress: Float, message: String) {
    val animatedProgress by animateFloatAsState(
        targetValue = progress,
        animationSpec = tween(durationMillis = 220, easing = LinearEasing),
        label = "fontLoadProgress",
    )

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            CircularProgressIndicator(
                modifier = Modifier.size(56.dp),
                strokeWidth = 4.dp,
            )
            Spacer(Modifier.height(24.dp))
            Text(
                text = "Loading font resources",
                style = MaterialTheme.typography.titleMedium,
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = message,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(Modifier.height(20.dp))
            LinearProgressIndicator(
                progress = { animatedProgress },
                modifier = Modifier
                    .fillMaxWidth(0.6f)
                    .height(6.dp),
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = "${(animatedProgress * 100).toInt()}%",
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun ErrorScreen(message: String, onRetry: () -> Unit) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = "Failed to load fonts",
                style = MaterialTheme.typography.titleMedium,
                color = Color(0xFFD32F2F),
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = message,
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth(0.9f)
                    .wrapContentWidth(Alignment.Start),
            )
            Spacer(Modifier.height(20.dp))
            Button(onClick = onRetry) {
                Text("Retry")
            }
        }
    }
}

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    ComposeViewport {
        val fontFamilyResolver = LocalFontFamilyResolver.current
        var loaded by remember { mutableStateOf(false) }
        var error by remember { mutableStateOf<String?>(null) }
        var progress by remember { mutableFloatStateOf(0f) }
        var progressMessage by remember { mutableStateOf("Initializing…") }
        var lastProgressMessage by remember { mutableStateOf("Initializing…") }
        var retryKey by remember { mutableStateOf(0) }

        LaunchedEffect(retryKey) {
            error = null
            loaded = false
            progress = 0f
            progressMessage = "Initializing…"
            lastProgressMessage = "Initializing…"
            try {
                loadFonts(fontFamilyResolver) { p, msg ->
                    progress = p
                    progressMessage = msg
                    lastProgressMessage = msg
                }
                loaded = true
            } catch (e: Throwable) {
                error = buildString {
                    append("Last stage: ").append(lastProgressMessage).append('\n')
                    append("Type: ").append(e::class.simpleName ?: "Throwable").append('\n')
                    append("Message: ").append(e.message ?: "(no message)")
                    e.cause?.let { c ->
                        append('\n')
                        append("Caused by: ")
                            .append(c::class.simpleName ?: "Throwable")
                            .append(": ")
                            .append(c.message ?: "(no message)")
                    }
                }
            }
        }

        AnimatedContent(
            targetState = when {
                error != null -> Stage.Error
                loaded -> Stage.Ready
                else -> Stage.Loading
            },
            transitionSpec = {
                fadeIn(animationSpec = tween(220)) togetherWith
                    fadeOut(animationSpec = tween(180))
            },
            label = "bootStage",
        ) { stage ->
            when (stage) {
                Stage.Loading -> LoadingScreen(progress, progressMessage)
                Stage.Error -> ErrorScreen(
                    message = error.orEmpty(),
                    onRetry = { retryKey++ },
                )
                Stage.Ready -> App()
            }
        }
    }
}

private enum class Stage { Loading, Ready, Error }
